import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogsAPI } from '../services/api';
import { Blog } from '../types';
import { Calendar, Eye, User, ArrowLeft } from 'lucide-react';
import ShareButton from '../components/ShareButton';
import { useAuth } from '../context/AuthContext';

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const canSeeViews = user?.role === 'admin' || user?.role === 'content-creator';
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await blogsAPI.getBySlug(slug!);
      setBlog(response.data.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Blog not found</h2>
          <Link to="/blogs" className="text-blue-600 hover:underline">Back to blogs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition">
            <ArrowLeft className="w-4 h-4" />
            Back to all articles
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sidebar */}
          <aside className="lg:col-span-3 space-y-6">
            {/* Category */}
            <div>
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wide rounded">
                {blog.category.replace('-', ' ')}
              </span>
              {blog.isTrending && (
                <span className="inline-block ml-2 px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold uppercase tracking-wide rounded">
                  🔥 Trending
                </span>
              )}
            </div>

            {/* Meta Info */}
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{blog.author?.firstName} {blog.author?.lastName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(blog.publishedAt || blog.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              {canSeeViews && (
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  <span>{blog.views} views</span>
                </div>
              )}
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-3">Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <span key={index} className="text-xs text-gray-600 hover:text-gray-900 cursor-pointer">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share - sidebar */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-3">Share</h3>
              <ShareButton
                url={`/blogs/${blog.slug}`}
                title={blog.title}
                description={blog.excerpt}
                align="left"
              />
            </div>
          </aside>

          {/* Main Article */}
          <div className="lg:col-span-9">
            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-gray-600 leading-relaxed mb-8 font-light italic border-l-4 border-blue-600 pl-6">
              {blog.excerpt}
            </p>

            {/* Featured Image */}
            {blog.featuredImage && (
              <figure className="mb-10">
                <img
                  src={blog.featuredImage}
                  alt={blog.title}
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </figure>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div 
                className="text-gray-800 leading-relaxed"
                style={{ 
                  fontSize: '1.125rem', 
                  lineHeight: '1.8',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {blog.content}
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
              <Link
                to="/blogs"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
              <ShareButton
                url={`/blogs/${blog.slug}`}
                title={blog.title}
                description={blog.excerpt}
                align="right"
              />
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
